import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { motion } from 'framer-motion'
import { Eye, EyeOff } from 'lucide-react'
import { useAuth } from '../../shared/auth'
import { useApiError } from '../../shared/hooks/useApiError'
import { MessageDialog } from '../../shared/ui'
import toast from 'react-hot-toast'
import { getOrGenerateDeviceId, getDeviceIdInfo } from '../../shared/utils/deviceIdGenerator'

const AppLogin = () => {
  const navigate = useNavigate()
  const { login } = useAuth()
  
  // UI状態
  const [showPassword, setShowPassword] = useState(false) // パスワード表示フラグ
  const [isLoading, setIsLoading] = useState(false) // ローディング状態
  const [deviceId, setDeviceId] = useState('') // デバイスID
  
  // APIエラーハンドリングフック
  const { 
    showErrorDialog,
    errorMessage,
    handleApiError,
    closeErrorDialog
  } = useApiError(() => setIsLoading(false))

  // フォーム管理
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm()

  // デバイスID初期化
  React.useEffect(() => {
    const initializeDeviceId = () => {
      try {
        const deviceId = getOrGenerateDeviceId()
        setDeviceId(deviceId)
        
        // デバイスID情報の取得と検証
        const deviceInfo = getDeviceIdInfo(deviceId)
        
        // デバイスID長さの検証
        if (deviceInfo && !deviceInfo.isWithinSNSLimit) {
          // Amazon SNS制限を超える場合は警告（ログは削除）
        }
      } catch (error) {
        // フォールバック用のシンプルなデバイスID
        const fallbackId = Date.now().toString(16)
        setDeviceId(fallbackId)
        localStorage.setItem('deviceId', fallbackId)
      }
    }
    
    initializeDeviceId()
  }, [])

  // ログイン処理
  const onSubmit = async (data) => {
    setIsLoading(true)
    try {
      // ログインデータにデバイスIDを追加
      const loginData = {
        ...data,
        deviceId: deviceId
      }
      const response = await login(loginData)
      // ネイティブアプリにUUIDを登録
      if (response && response.uuid) {
        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.registerUserID) {
          window.webkit.messageHandlers.registerUserID.postMessage(response.uuid)
        } else if (window.registerUserID) {
          window.registerUserID.postMessage(response.uuid)
        }
      }
      toast.success('ログインに成功しました！')
      navigate('/appTop')
    } catch (error) {
      handleApiError(new Error('ログインに失敗しました。認証情報を確認してください。'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-white flex flex-col p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="flex-1 flex flex-col items-center justify-center"
      >
        {/* Logo */}
        <motion.div 
          className="mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, ease: "linear" }}
        >
          <img 
            src="/core/img/pix-label-logo.png" 
            alt="pix-label" 
            className="w-full max-w-sm mx-auto"
          />
        </motion.div>

        {/* Login Form */}
        <div className="w-full max-w-sm space-y-4">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Email Address */}
            <div>
              <input
                type="text"
                {...register('mailAddress', { 
                  required: 'メールアドレスを入力してください。',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: '有効なメールアドレスを入力してください。'
                  }
                })}
                className="w-[95%] block mx-auto px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="メールアドレス"
                autoCapitalize="off"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleSubmit(onSubmit)()
                  }
                }}
              />
              {errors.mailAddress && (
                <p className="text-red-500 text-sm mt-1">{errors.mailAddress.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', { required: 'パスワードを入力してください。' })}
                  className="w-[95%] block mx-auto px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent pr-12"
                  placeholder="パスワード"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleSubmit(onSubmit)()
                    }
                  }}
                />
                {/* Password Toggle Button */}
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-[2.5%] top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-transparent"
                  title={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
                  aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
                >
                  {showPassword ? (
                    <Eye className="h-5 w-5" />
                  ) : (
                    <EyeOff className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
              )}
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-[90%] block mx-auto py-3 px-6 bg-gray-800 text-white rounded-full font-medium tracking-widest hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-busy={isLoading}
              aria-label={isLoading ? 'ログイン中...' : 'ログイン'}
            >
              {isLoading ? 'ログイン中...' : 'ログイン'}
            </button>


            {/* Links */}
            <div className="space-y-2 text-sm">
              <div className="text-right">
                <a 
                  href="/appSendResetPassword" 
                  className="text-blue-600 hover:text-blue-800"
                >
                  パスワードを忘れた場合
                </a>
              </div>
              <div className="text-right">
                <a 
                  href="/appInquiry" 
                  className="text-blue-600 hover:text-blue-800"
                >
                  お問い合わせ
                </a>
              </div>
            </div>
          </form>
        </div>
      </motion.div>

      {/* Footer */}
      <div className="text-center fixed bottom-0 w-full h-10 flex items-center justify-center">
        <div className="text-center">
          <span className="text-gray-600">アカウントを持っていない場合</span>
          <a 
            href="/appKaiinToroku" 
            className="text-red-600 font-bold ml-2 hover:text-red-700"
          >
            無料登録はこちら
          </a>
        </div>
      </div>

      {/* Error Dialog */}
      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title="メッセージ"
        message={errorMessage}
        type="error"
      />
    </div>
  )
}

export default AppLogin

